import { useEffect, useState } from 'react';
import { auditApi } from '../../lib/api';
import { ErrorBanner } from '../../components/ErrorBanner';
import { EmptyState } from '../../components/EmptyState';
import type { AuditEntry } from '../../lib/types';

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function diffFields(before: Record<string, unknown> | null, after: Record<string, unknown> | null) {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  return [...keys].map((key) => ({
    key,
    before: before?.[key],
    after: after?.[key],
    changed: JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]),
  }));
}

interface EditLogPanelProps {
  table: string;
  recordId: string;
}

/**
 * Per-record audit trail — reachable from any master. This is a compliance
 * selling point (PRD §5.9): the field-level before/after diff needs to look
 * like evidence an auditor would accept, not a debug log.
 */
export function EditLogPanel({ table, recordId }: EditLogPanelProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    auditApi
      .record(table, recordId)
      .then(({ items }) => setEntries(items))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [table, recordId]);

  if (loading) return <p className="text-[12px] text-[var(--text-dim)]">Loading edit log…</p>;
  if (error) return <ErrorBanner error={error} />;
  if (entries.length === 0) return <EmptyState title="No edit history for this record." />;

  return (
    <div className="flex flex-col gap-3">
      {entries.map((entry) => (
        <div key={entry.id} className="border border-[var(--border)]">
          <div className="flex items-center justify-between bg-[var(--bg-inset)] px-2 py-1 text-[11.5px]">
            <span className="font-medium uppercase tracking-wide text-[var(--text-dim)]">{entry.action}</span>
            <span className="text-[var(--text-dim)]">{new Date(entry.occurredAt).toLocaleString('en-IN')}</span>
          </div>
          <div className="px-2 py-1.5 text-[12px]">
            <p className="mb-1 text-[11.5px] text-[var(--text-faint)]">
              {entry.actorMissing ? (
                <span className="text-[var(--warn)]">Actor not recorded (system operation)</span>
              ) : (
                <>By <span className="text-[var(--text)]">{entry.actorName ?? entry.actorUserId}</span></>
              )}
            </p>
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wide text-[var(--text-faint)]">
                  <th className="w-1/4 pb-1 text-left font-medium">Field</th>
                  <th className="pb-1 text-left font-medium">Before</th>
                  <th className="pb-1 text-left font-medium">After</th>
                </tr>
              </thead>
              <tbody>
                {diffFields(entry.before, entry.after).map((f) => (
                  <tr key={f.key} className="border-t border-[var(--border)]">
                    <td className="py-1 pr-2 mono text-[11.5px] text-[var(--text-dim)]">{f.key}</td>
                    <td className={`py-1 pr-2 ${f.changed ? 'text-[var(--danger)] line-through decoration-1' : ''}`}>{formatValue(f.before)}</td>
                    <td className={`py-1 ${f.changed ? 'font-medium text-[var(--success)]' : ''}`}>{formatValue(f.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
